import type { ComponentProps } from 'react';
import React, { useCallback } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
  BodyText,
  Button,
  CloudinaryImage,
  Modal,
  NoticeBannersStack,
  NoticeBannersStackProvider,
  TagAlert,
  theme,
} from '@sg/garnish';

import { useLocalizationContext } from '@order/Localization';

import { LOCATION_CARD_IMG_BG_COLOR } from '../../../../LocationResultsV2.constants';
import type {
  Location,
  OnSelectedLocationCardPress,
} from '../../LocationResultCard.types';
import { LocationResultCardCTAs } from '../LocationResultCardCTAs';

// ─────────────────────────────────────────────────────────────────────────────

export const LocationResultCardDetailsModal = (
  props: LocationResultCardModalProps,
) => {
  const {
    location,
    locationSearchType,
    addressDetails,
    visible,
    isLoading = false,
    isDisabled = false,
    onRequestClose,
    onOrderNowBtnPress,
  } = props;
  const {
    name,
    imageUrl,
    acceptingOrders: isAcceptingOrders = true,
    isClosed,
    flexMessage,
    notAcceptingOrdersReason,
  } = location;

  const { t } = useLocalizationContext();
  const { bottom: safeAreaBottom } = useSafeAreaInsets();

  const ctaLabelForPickupOutpostLocation = isAcceptingOrders
    ? t('location.order-now')
    : t('location.closed');
  const ctaLabelForDeliveryLocation = isClosed
    ? t('delivery.order-later')
    : t('delivery.order-now');
  const ctaLabel =
    locationSearchType === 'delivery'
      ? ctaLabelForDeliveryLocation
      : ctaLabelForPickupOutpostLocation;

  // ─── Helpers ─────────────────────────────────────────────────────────

  const handleOrderNowBtnPress = useCallback(() => {
    onOrderNowBtnPress(location);
  }, [location, onOrderNowBtnPress]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Modal
      visible={visible}
      onRequestClose={onRequestClose}
      animationType="fade"
    >
      <NoticeBannersStackProvider>
        <Modal.CloseBtn onPress={onRequestClose} />

        <ScrollView>
          {/*  ─── Location Image ────────────────────────────────────────────────── */}

          {imageUrl ? (
            <Modal.Row>
              <View
                style={[styles.imgContainer, styles.imgContainerWithBgColor]}
              >
                <CloudinaryImage
                  baseUrl={imageUrl}
                  resizeMode="cover"
                  style={styles.img}
                  accessibilityIgnoresInvertColors
                  accessibilityLabel={name}
                  config={IMG_CLOUDINARY_CONFIG}
                />
              </View>
            </Modal.Row>
          ) : null}

          {/*  ─── Location Info ─────────────────────────────────────────────────── */}

          <Modal.Row>
            <BodyText bold size={1} style={styles.sectionText}>
              {name}
            </BodyText>

            {!isAcceptingOrders ? (
              <LocationClosedNotice
                notAcceptingOrdersReason={notAcceptingOrdersReason}
              />
            ) : null}

            <LocationInfo location={location} addressDetails={addressDetails} />

            {flexMessage ? (
              <LocationFlexMessage flexMessage={flexMessage} />
            ) : null}
          </Modal.Row>

          <Modal.Row style={styles.ctaContainer}>
            <LocationResultCardCTAs location={location} />
          </Modal.Row>
        </ScrollView>

        {/*  ───────────────────────────────────────────────────────────────────── */}

        <Modal.Footer>
          <Button
            onPress={handleOrderNowBtnPress}
            size="large"
            isLoading={isLoading}
            disabled={isDisabled || !isAcceptingOrders}
          >
            {ctaLabel}
          </Button>
        </Modal.Footer>

        <View style={styles.noticeBannersStack}>
          <NoticeBannersStack
            bannerPadding={{
              top: theme.spacing.large,
              right: theme.spacing.medium,
              bottom: Math.max(
                safeAreaBottom + theme.spacing.small,
                theme.spacing.large,
              ),
              left: theme.spacing.medium,
            }}
          />
        </View>
      </NoticeBannersStackProvider>
    </Modal>
  );
};

// ─── Subcomponents ───────────────────────────────────────────────────────────

const LocationClosedNotice = (
  props: Pick<Location, 'notAcceptingOrdersReason'>,
) => {
  const { notAcceptingOrdersReason } = props;

  const { t } = useLocalizationContext();

  return (
    <>
      <BodyText style={[styles.closedLabel, styles.sectionText]}>
        {t('location.closed')}
      </BodyText>

      {notAcceptingOrdersReason ? (
        <View style={styles.sectionText}>
          <TagAlert size="small" palette="caution">
            {notAcceptingOrdersReason}
          </TagAlert>
        </View>
      ) : null}
    </>
  );
};

const LocationFlexMessage = (props: Pick<Location, 'flexMessage'>) => {
  const { flexMessage } = props;

  return (
    <View style={styles.sectionText}>
      <TagAlert size="small" palette="cautionDark">
        {flexMessage}
      </TagAlert>
    </View>
  );
};

const LocationInfo = (
  props: Pick<LocationResultCardModalProps, 'addressDetails' | 'location'>,
) => {
  const { addressDetails, location } = props;
  const { address, storeHours, estimatedDeliveryTime } = location;

  return (
    <View>
      <BodyText>{address}</BodyText>

      {addressDetails ? (
        <BodyText style={styles.sectionText}>{addressDetails}</BodyText>
      ) : null}

      {storeHours ? (
        <BodyText style={styles.sectionText}>{storeHours}</BodyText>
      ) : null}

      {estimatedDeliveryTime ? (
        <BodyText style={styles.sectionText}>{estimatedDeliveryTime}</BodyText>
      ) : null}
    </View>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const IMG_CLOUDINARY_CONFIG = { height: 200, crop: 'fill' } as const;

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  imgContainer: {
    width: '100%',
    borderRadius: theme.radius.medium,
    marginBottom: theme.spacing.medium,
  },
  imgContainerWithBgColor: {
    backgroundColor: LOCATION_CARD_IMG_BG_COLOR,
  },
  img: {
    width: '100%',
    height: 200,
    borderRadius: theme.radius.medium,
  },
  closedLabel: {
    color: theme.colors.CAUTION,
  },
  sectionText: {
    marginBottom: theme.spacing.small,
  },
  ctaContainer: {
    paddingVertical: theme.spacing.small,
  },
  noticeBannersStack: {
    ...StyleSheet.absoluteFillObject,
    top: 'auto',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type LocationResultCardModalProps = {
  readonly location: Location;
  readonly addressDetails?: string;
  readonly isLoading?: boolean;
  readonly isDisabled?: boolean;
  readonly locationSearchType: 'pickup' | 'delivery' | 'outpost';
  readonly onOrderNowBtnPress: OnSelectedLocationCardPress;
} & Pick<ComponentProps<typeof Modal>, 'visible' | 'onRequestClose'>;
